import { OpenInNew, Warning } from "@mui/icons-material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import {
  Box,
  Divider,
  Link,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import Card from "@mui/material/Card";
import CircularProgress from "@mui/material/CircularProgress";
import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import { useTheme } from "@mui/material/styles";
import axios from "axios";
import Attachments from "components/Attachments";
import ConvertibleInput from "components/ConvertibleInput";
import Deleted from "components/Deleted";
import LoadingButton from "components/LoadingButton";
import MDBadge from "components/MDBadge";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDSelect from "components/MDSelect";
import MDTypography from "components/MDTypography";
import { useLanguage } from "contexts/language.context";
import DataTable from "examples/Tables/DataTable/index";
import * as jsondiffpatch from "jsondiffpatch";
import moment from "moment";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-hot-toast";
import { Link as RouterLink, useParams } from "react-router-dom";
import auditsService from "services/audits-service";
import branchesService from "services/branches-service";
import {
  DEV_ANSWER_TYPE_ID,
  DONT_GENERATE_TASKS_STATUS_IDS,
  GENERATE_REPORT_STATUS_IDS,
  STATUS_COLOR,
  STD_ANSWER_TYPE_ID,
} from "../../../../const";
import AuditGenerateReportDialog from "../AssessmentReport/AuditGenerateReportDialog";
import AuditInfoDialogv2 from "./dialogs/AuditInfoDialog";
import AuditKpisDialogv2 from "./dialogs/AuditKpisDialog";
import AuditSummaryGroupsDialog from "./dialogs/AuditSummaryGroupsDialog";
import TasksDialog from "./dialogs/TasksDialog";
import useObjectState from "utils/useObjectState";
import { usePermissionMonitor } from "#components/PermissionMonitor/index.js";
import AuditReceiptDialog from "./dialogs/AuditReceiptDialog";
import AuditNarrativeDialog from "./dialogs/AuditNarrativeDialog";

const CustomizedMenus = ({
  loading,
  setLoading,
  statuses,
  currentStatus,
  onChange,
  readOnly,
}) => {
  const [anchorEl, setAnchorEl] = React.useState(null);
  const open = Boolean(anchorEl);
  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleStatusChange = async (optionId) => {
    handleClose();
    setLoading(true);
    await onChange(optionId);
    setLoading(false);
  };

  return (
    <div>
      <MDBadge
        badgeContent={
          <>
            {currentStatus.description}
            {readOnly ? null : <KeyboardArrowDownIcon />}
          </>
        }
        variant="gradient"
        color={STATUS_COLOR[currentStatus.id]?.preset ?? null}
        container
        size="lg"
        sx={{
          opacity: loading ? ".5" : "1",
          pointerEvents: loading || readOnly ? "none" : "",
          cursor: loading || readOnly ? "none" : "pointer",
          borderRadius: "12px !important",
        }}
        onClick={readOnly ? null : handleClick}
        aria-haspopup="true"
      ></MDBadge>
      <Menu
        id="demo-customized-menu"
        MenuListProps={{
          "aria-labelledby": "demo-customized-button",
        }}
        anchorEl={anchorEl}
        open={readOnly ? false : open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
      >
        {statuses
          ? statuses.map((status, index) => (
              <MenuItem
                key={index}
                onClick={() => handleStatusChange(status.id)}
                sx={{
                  p: 1,
                  px: 2,
                  m: 0,
                  mx: -1,
                  display: "flex",
                  flexDirection: "row",
                }}
              >
                {status.description}
              </MenuItem>
            ))
          : null}
      </Menu>
    </div>
  );
};

export default function Audit({ setRoute, permissions }) {
  const { label } = useLanguage();
  const { id } = useParams();
  const theme = useTheme();
  const can = usePermissionMonitor();

  const [audit, setAudit] = useState(null);
  const [oldAudit, setOldAudit] = useState(null);

  const [flatPillars, setFlatPillars] = useState(null);
  const [oldFlatPillars, setOldFlatPillars] = useState(null);
  const [pillarRows, setPillarRows] = useState(null);

  const [loading, setLoading] = useState(false);
  const [loadingReport, setLoadingReport] = useState(false);
  const [kpiZoom, setKpiZoom] = useState(1);

  const [open, changeOpen] = useObjectState({
    info: false,
    summaryGroups: false,
    assessmentReport: false,
    kpis: false,
    tasks: false,
  });

  const [options, changeOptions] = useObjectState({
    contacts: null,
    statuses: null,
  });
  const contactsRequestInFlight = React.useRef(false);

  const [auditState, setAuditState] = useState(null);

  permissions.update = can("update", "audit", audit);

  async function fetch(cancelToken) {
    setLoading(true);

    const [response, data] = await auditsService.getAuditCore(id, cancelToken);
    if (response.ok) {
      if (!data) {
        setAuditState("deleted");
      } else {
        setAudit(data);
        console.log("AUDIT: ", data);
        setOldAudit({ ...data, pillars: [...data.pillars] });

        setAuditState("ok");
      }
    } else {
      console.log(`Error while fetching audit: ${data.error}`);
    }
    setLoading(false);
    return response.ok ? data : null;
  }

  async function fetchContacts() {
    if (audit && !options.contacts && !contactsRequestInFlight.current) {
      contactsRequestInFlight.current = true;
      const [response, data] = await branchesService.getContactsOfBranch(
        audit.branchId,
      );
      if (response.ok) {
        changeOptions("contacts", data || []);
      } else {
        console.log(`Error while fetching contact branches: ${data.error}`);
      }
      contactsRequestInFlight.current = false;
    }
  }

  async function fetchStatuses() {
    if (options.statuses) return;

    const [response, data] = await auditsService.getStatuses();
    if (response.ok) {
      changeOptions("statuses", data);
    } else {
      console.log(`Error while fetching statuses: ${data.error}`);
    }
  }

  async function save(flatPillars, oldFlatPillars, audit) {
    const promise = handleUpdateAllKpis(flatPillars, oldFlatPillars, audit);

    toast.promise(promise, {
      position: "bottom-right",
      loading: label("loading"),
      success: label("saved"),
      error: label("not-saved"),
      duration: 5,
    });

    try {
      await promise;
    } catch (err) {}
  }

  function updateRows(flatPillars) {
    function toggleCollapse(index) {
      const flat = [...flatPillars];
      flat[index].collapsed = !flat[index].collapsed;
      setFlatPillars(flat);
    }

    const collapsedParentIds = [];

    flatPillars.map((pillar, index) => {
      const isKpi = pillar.pillars ? false : true;
      if (!isKpi && !collapsedParentIds.includes(pillar.parentId)) {
        if (pillar.collapsed) {
          collapsedParentIds.push(pillar.id);
        }
      }
    });

    const f = flatPillars.map((pillar, index) => {
      const kpi = pillar;
      const isKpi = pillar.pillars ? false : true;
      const isPillarParent = pillar.pillars?.length > 0;
      const isKpiParent = pillar.kpis?.length > 0;

      const hasChildren = isPillarParent || isKpiParent;

      if (!isKpi && !collapsedParentIds.includes(pillar.parentId)) {
        return {
          description: (
            <Grid container sx={{ alignItems: "center" }}>
              <Grid item sx={{ width: pillar.level * 40 }}></Grid>
              <Grid item>
                {hasChildren ? (
                  <IconButton
                    size="small"
                    onClick={() => {
                      toggleCollapse(index);
                    }}
                  >
                    <Icon>
                      {pillar.collapsed
                        ? "keyboard_arrow_right"
                        : "keyboard_arrow_down"}
                    </Icon>
                  </IconButton>
                ) : (
                  <IconButton sx={{ pointerEvents: "none" }} size="small">
                    <Icon></Icon>
                  </IconButton>
                )}
              </Grid>

              <Grid
                item
                sx={{
                  cursor: hasChildren ? "pointer" : "auto",
                  overflow: "hidden",
                  flex: "1",
                }}
                onClick={() => {
                  if (hasChildren) toggleCollapse(index);
                }}
              >
                <MDTypography
                  variant="inherit"
                  sx={{
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  <MDBadge
                    circular
                    badgeContent={label("pillar-singular").toUpperCase()}
                    variant="gradient"
                    color="primary"
                    container
                  />
                  &nbsp;&nbsp;
                  {pillar.description}
                </MDTypography>
              </Grid>
            </Grid>
          ),
          eliminatory: (
            <Typography
              variant="body2"
              color="primary"
              sx={{ fontWeight: "500", fontSize: "14px" }}
            >
              {pillar.kpis.reduce((prev, curr) => {
                return (
                  prev ||
                  (curr.auditKpis[0].eliminatory &&
                    curr.auditKpis[0].kpiValueTypeAnswerId ==
                      DEV_ANSWER_TYPE_ID)
                );
              }, false)
                ? "Eliminated"
                : ""}
            </Typography>
          ),
          // weight: pillar.kpis.reduce((prev, curr) => {
          //   return (
          //     prev ||
          //     (curr.auditKpis[0].eliminatory &&
          //       curr.auditKpis[0].kpiValueTypeAnswerId == DEV_ANSWER_TYPE_ID)
          //   );
          // }, false)
          //   ? 0
          //   : pillar.kpis.reduce((prev, curr) => {
          //       let weight = curr.auditKpis[0].weight || curr.weight;
          //       if (
          //         curr.auditKpis[0].kpiValueTypeAnswerId != STD_ANSWER_TYPE_ID
          //       ) {
          //         weight = 0;
          //       }
          //       return prev + weight;
          //     }, 0),
          key: `Datatablecell: ${pillar.id}`,
        };
      } else if (isKpi && !collapsedParentIds.includes(kpi.pillarId)) {
        return {
          key: `Datatablecell: ${kpi?.id} ${kpi?.pillarId} ${kpi?.auditKpis[0].id}`,
          description: (
            <MDBox
              display="flex"
              flexDirection="column"
              gap={1}
              key={`Description for ${pillar.id} ${index}`}
              sx={{ flex: 1, width: "100%" }}
            >
              <MDBox display="flex" flexDirection="row" sx={{ width: "100%" }}>
                <MDBox sx={{ width: kpi.level * 40 }}></MDBox>
                <MDBox
                  display="flex"
                  flexDirection="row"
                  alignItems="center"
                  sx={{
                    flex: 1,
                    width: "100%",
                    gap: "6px",
                    overflow: "hidden",
                  }}
                >
                  <MDBadge
                    circular
                    badgeContent={
                      <>
                        {label("kpi-singular").toUpperCase()}
                        {kpi.eliminatory ? (
                          <>
                            <Box sx={{ width: "4px" }} />
                            <Warning />{" "}
                          </>
                        ) : null}
                      </>
                    }
                    variant="gradient"
                    color="info"
                    container
                  />
                  <ConvertibleInput
                    readOnly={true || !kpi?.editing?.description}
                    fullWidth={true}
                    key={`convertible input: ${kpi?.id} ${kpi?.pillarId} ${kpi?.auditKpis[0].id}`}
                    sx={{
                      flex: 1,
                      whiteSpace: "wrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      display: "-webkit-box",
                      WebkitLineClamp: "3",
                      WebkitBoxOrient: "vertical",
                    }}
                    multiline
                    rows={2}
                    typographyProps={{
                      variant: "inherit",
                      color: "dark",
                    }}
                    label={label("description-singular")}
                    readValue={kpi.auditKpis[0].description || ""}
                    value={kpi.auditKpis[0].allLanguageDescription || ""}
                    waitForSave={true}
                    onSave={kpi?.saveDescription}
                    onCancel={kpi?.cancelDescription}
                    onChange={(e) => {
                      const newFlatPillars = [...flatPillars];
                      newFlatPillars[
                        index
                      ].auditKpis[0].allLanguageDescription = e.target.value;
                      setFlatPillars(newFlatPillars);
                    }}
                  />
                </MDBox>

                <MDBox>
                  {false &&
                    permissions?.update &&
                    !kpi?.editing?.description && (
                      <IconButton
                        size="small"
                        onClick={() => {
                          const newFlatPillars = [...flatPillars];
                          if (!newFlatPillars[index].editing)
                            newFlatPillars[index].editing = {};
                          newFlatPillars[index].editing.description = true;
                          setFlatPillars(newFlatPillars);
                        }}
                      >
                        <Icon>edit</Icon>
                      </IconButton>
                    )}
                  {kpi?.editing?.description && (
                    <>
                      {!flatPillars[index].loading && (
                        <IconButton
                          size="small"
                          onClick={() => {
                            const newFlatPillars = [...flatPillars];
                            newFlatPillars[index].cancelDescription =
                              Math.random();
                            if (!newFlatPillars[index].editing)
                              newFlatPillars[index].editing = {};
                            newFlatPillars[index].editing.description = false;
                            setFlatPillars(newFlatPillars);
                          }}
                        >
                          <Icon>close</Icon>
                        </IconButton>
                      )}
                      <IconButton
                        disabled={flatPillars[index].loading}
                        size="small"
                        onClick={async () => {
                          const newFlatPillars = [...flatPillars];
                          //newFlatPillars[index].loading = true;
                          newFlatPillars[index].saveDescription = Math.random();
                          if (!newFlatPillars[index].editing)
                            newFlatPillars[index].editing = {};
                          newFlatPillars[index].editing.description = false;
                          setFlatPillars(newFlatPillars);

                          //handleUpdateAllKpis(newFlatPillars);
                        }}
                      >
                        {flatPillars[index].loading && (
                          <CircularProgress
                            size={20}
                            thickness={6}
                            color="info"
                          />
                        )}
                        {!flatPillars[index].loading && <Icon>save</Icon>}
                      </IconButton>
                    </>
                  )}
                </MDBox>
              </MDBox>
            </MDBox>
          ),
          eliminatory: kpi.kpiValueTypeId ? (
            <Box>
              <Switch
                checked={!!kpi?.auditKpis[0].eliminatory}
                onChange={(e) => {
                  const newFlatPillars = [...flatPillars];
                  newFlatPillars[index].auditKpis[0].eliminatory =
                    e.target.checked;
                  setFlatPillars(newFlatPillars);
                }}
              />
            </Box>
          ) : null,
          weight: (
            <ConvertibleInput
              readOnly={!kpi?.editing?.weight}
              fullWidth={true}
              size="small"
              type="number"
              key={`weight convertible input: ${kpi?.id} ${kpi?.pillarId} ${kpi?.auditKpis[0].id}`}
              sx={{
                flex: 1,
                whiteSpace: "wrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                display: "-webkit-box",
                WebkitLineClamp: "3",
                WebkitBoxOrient: "vertical",
                minWidth: "80px",
                width: "100%",
              }}
              multiline
              rows={2}
              typographyProps={{
                variant: "inherit",
                color: "dark",
              }}
              value={
                !kpi?.editing?.weight
                  ? (kpi.auditKpis[0].weight ?? kpi.weight ?? "")
                  : (kpi.auditKpis[0].weightTemp ??
                    kpi.auditKpis[0].weight ??
                    kpi.weight ??
                    "")
              }
              waitForSave={true}
              onSave={kpi?.saveWeight}
              onCancel={kpi?.cancelWeight}
              onChange={(e) => {
                const newFlatPillars = [...flatPillars];
                newFlatPillars[index].auditKpis[0].weight = Number(
                  e.target.value,
                );
                setFlatPillars(newFlatPillars);
              }}
              includeUpdate={
                permissions?.update
                  ? {
                      cancelButtonProps: {
                        type: "icon",

                        onClick: () => {
                          const newFlatPillars = [...flatPillars];
                          newFlatPillars[index].cancelWeight = Math.random();
                          if (!newFlatPillars[index].editing)
                            newFlatPillars[index].editing = {};
                          newFlatPillars[index].editing.weight = false;
                          setFlatPillars(newFlatPillars);
                        },

                        disabled: flatPillars[index].loading,
                      },
                      saveButtonProps: {
                        type: "icon",
                        onClick: async () => {
                          const newFlatPillars = [...flatPillars];
                          //newFlatPillars[index].loading = true;
                          newFlatPillars[index].saveWeight = Math.random();
                          if (!newFlatPillars[index].editing)
                            newFlatPillars[index].editing = {};
                          newFlatPillars[index].editing.weight = false;
                          setFlatPillars(newFlatPillars);
                        },
                        disabled: flatPillars[index].loading,
                      },
                      editButtonProps: {
                        onClick: () => {
                          const newFlatPillars = [...flatPillars];
                          if (!newFlatPillars[index].editing)
                            newFlatPillars[index].editing = {};
                          newFlatPillars[index].editing.weight = true;
                          setFlatPillars(newFlatPillars);
                        },
                      },
                    }
                  : null
              }
            />
          ),
          answer:
            kpi.kpiValueTypeId == 4 ? (
              <ConvertibleInput
                readOnly={!kpi?.editing?.answerValue}
                fullWidth={true}
                sx={{
                  flex: 1,
                  gap: "6px",
                  whiteSpace: "wrap",
                  textAlign: "start",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  display: "-webkit-box",
                  WebkitLineClamp: "3",
                  WebkitBoxOrient: "vertical",
                  minWidth: "200px",
                  width: "100%",
                }}
                multiline
                rows={2}
                typographyProps={{
                  variant: "inherit",
                  color: "dark",
                }}
                label={label("answer-singular")}
                waitForSave={true}
                onSave={kpi?.saveAnswerValue}
                onCancel={kpi?.cancelAnswerValue}
                value={
                  !kpi?.editing?.remarks
                    ? kpi.auditKpis[0].answerValue || ""
                    : kpi.auditKpis[0].answerValueTemp ||
                      kpi.auditKpis[0].answerValue ||
                      ""
                }
                onChange={(e) => {
                  const newFlatPillars = [...flatPillars];
                  newFlatPillars[index].auditKpis[0].answerValue =
                    e.target.value;
                  setFlatPillars(newFlatPillars);
                }}
                includeUpdate={
                  permissions?.update
                    ? {
                        editButtonProps: {
                          onClick: () => {
                            const newFlatPillars = [...flatPillars];
                            if (!newFlatPillars[index].editing)
                              newFlatPillars[index].editing = {};
                            newFlatPillars[index].editing.answerValue = true;
                            setFlatPillars(newFlatPillars);
                          },
                        },
                        saveButtonProps: {
                          onClick: async () => {
                            const newFlatPillars = [...flatPillars];
                            //newFlatPillars[index].loading = true;
                            newFlatPillars[index].saveAnswerValue =
                              Math.random();
                            if (!newFlatPillars[index].editing)
                              newFlatPillars[index].editing = {};
                            newFlatPillars[index].editing.answerValue = false;
                            setFlatPillars(newFlatPillars);

                            //handleUpdateAllKpis(newFlatPillars);
                          },
                          disabled: flatPillars[index].loading,
                        },
                        cancelButtonProps: {
                          onClick: () => {
                            const newFlatPillars = [...flatPillars];
                            newFlatPillars[index].cancelAnswerValue =
                              Math.random();
                            if (!newFlatPillars[index].editing)
                              newFlatPillars[index].editing = {};
                            newFlatPillars[index].editing.answerValue = false;
                            setFlatPillars(newFlatPillars);
                          },
                        },
                      }
                    : null
                }
              />
            ) : (
              <MDSelect
                noneOption={!Boolean(kpi.auditKpis[0].kpiValueTypeAnswerId)}
                fullWidth
                disabled={!permissions?.update}
                label={label("answer-singular")}
                items={kpi.kpiValueType.kpiValueTypeAnswers.map((item) => ({
                  value: item.id,
                  render: item.description,
                }))}
                value={kpi.auditKpis[0].kpiValueTypeAnswerId || ""}
                onChange={(value) => {
                  const newFlatPillars = [...flatPillars];
                  newFlatPillars[index].auditKpis[0].kpiValueTypeAnswerId =
                    value;
                  setFlatPillars(newFlatPillars);
                }}
                sx={{ minWidth: 20, maxWidth: "300px", textAlign: "start" }}
              />
            ),
          selectDevRiskAction: (
            <React.Fragment
              key={`Select Devi risk action for ${pillar.id} ${index}`}
            >
              {kpi?.auditKpis[0]?.kpiValueTypeAnswerId ===
                4 /* IS DEVIATION */ && (
                <>
                  <Grid
                    container
                    direction="column"
                    spacing={1}
                    sx={{ minWidth: 200 }}
                  >
                    <Grid item xs={true}>
                      <MDSelect
                        disabled={!permissions?.update}
                        fullWidth
                        label={label("deviation-singular")}
                        // DEVIATION IS WHEN TYPE IS 2
                        items={kpi.kpiRiskActions
                          .filter((deviation) => deviation.type == 2)
                          .map((deviation) => ({
                            value: deviation.id,
                            render: deviation.description,
                          }))}
                        value={
                          kpi.auditKpis[0].kpiDeviationId ||
                          (() => {
                            const value =
                              kpi.kpiRiskActions.filter(
                                (deviation) =>
                                  deviation.type == 2 && deviation.isDefault,
                              )[0]?.id || "";

                            if (value != "") {
                              setFlatPillars((realFlatPillars) => {
                                const newFlatPillars = [...realFlatPillars];
                                newFlatPillars[
                                  index
                                ].auditKpis[0].kpiDeviationId = value;
                                return newFlatPillars;
                              });
                            }

                            return value;
                          })()
                        }
                        onChange={(value) => {
                          const newFlatPillars = [...flatPillars];
                          newFlatPillars[index].auditKpis[0].kpiDeviationId =
                            value;

                          setFlatPillars(newFlatPillars);
                        }}
                        sx={{
                          minWidth: 200,
                          maxWidth: "300px",
                          textAlign: "start",
                        }}
                      />
                    </Grid>
                    <Grid item xs={true}>
                      <MDSelect
                        disabled={!permissions?.update}
                        fullWidth
                        label={label("risk-singular")}
                        // RISK IS WHEN TYPE IS 0
                        items={kpi.kpiRiskActions
                          .filter((risk) => risk.type == 0)
                          .map((risk) => ({
                            value: risk.id,
                            render: risk.description,
                          }))}
                        value={
                          kpi.auditKpis[0].kpiRiskId ||
                          (() => {
                            const value =
                              kpi.kpiRiskActions.filter(
                                (risk) => risk.type == 0 && risk.isDefault,
                              )[0]?.id || "";

                            if (value != "") {
                              const newFlatPillars = [...flatPillars];
                              newFlatPillars[index].auditKpis[0].kpiRiskId =
                                value;
                              setFlatPillars([...newFlatPillars]);
                            }
                            return value;
                          })()
                        }
                        onChange={(value) => {
                          const newFlatPillars = [...flatPillars];
                          newFlatPillars[index].auditKpis[0].kpiRiskId = value;
                          setFlatPillars(newFlatPillars);
                        }}
                        sx={{
                          minWidth: 200,
                          maxWidth: "300px",
                          textAlign: "start",
                        }}
                      />
                    </Grid>
                    <Grid item xs={true}>
                      <MDSelect
                        disabled={!permissions?.update}
                        fullWidth
                        label={label("action-singular")}
                        // ACTION IS WHEN TYPE IS 1
                        items={kpi.kpiRiskActions
                          .filter((action) => action.type == 1)
                          .map((action) => ({
                            value: action.id,
                            render: action.description,
                          }))}
                        value={
                          kpi.auditKpis[0].kpiActionId ||
                          (() => {
                            const value =
                              kpi.kpiRiskActions.filter(
                                (action) =>
                                  action.type == 1 && action.isDefault,
                              )[0]?.id || "";

                            if (value != "") {
                              const newFlatPillars = [...flatPillars];
                              newFlatPillars[index].auditKpis[0].kpiActionId =
                                value;
                              setFlatPillars([...newFlatPillars]);
                            }

                            return value;
                          })()
                        }
                        onChange={(value) => {
                          const newFlatPillars = [...flatPillars];
                          newFlatPillars[index].auditKpis[0].kpiActionId =
                            value;
                          setFlatPillars([...newFlatPillars]);
                        }}
                        sx={{
                          minWidth: 200,
                          maxWidth: "300px",
                          textAlign: "start",
                        }}
                      />
                    </Grid>
                  </Grid>
                </>
              )}

              {kpi?.auditKpis[0]?.kpiValueTypeAnswerId !== 4 &&
                kpi?.kpiValueType?.kpiValueTypeAnswers.find(
                  (value) =>
                    value.id === (kpi.auditKpis[0].kpiValueTypeAnswerId || 3),
                )?.kpiAnswerReasons?.length > 0 && (
                  <>
                    <Grid container direction="column" spacing={1}>
                      <Grid item xs={true}>
                        <MDSelect
                          disabled={!permissions?.update}
                          fullWidth
                          label={label("reason-singular")}
                          // DEVIATION IS WHEN TYPE IS 2
                          items={kpi?.kpiValueType?.kpiValueTypeAnswers
                            .find(
                              (value) =>
                                value.id ===
                                (kpi.auditKpis[0].kpiValueTypeAnswerId || 3),
                            )
                            ?.kpiAnswerReasons.map((reason) => ({
                              value: reason.id,
                              render: reason.description,
                            }))}
                          value={kpi.auditKpis[0].kpiAnswerReasonId || ""}
                          onChange={(value) => {
                            const newFlatPillars = [...flatPillars];
                            newFlatPillars[
                              index
                            ].auditKpis[0].kpiAnswerReasonId = value;

                            setFlatPillars(newFlatPillars);
                          }}
                          sx={{
                            minWidth: 200,
                            maxWidth: "300px",
                            textAlign: "start",
                          }}
                        />
                      </Grid>
                    </Grid>
                  </>
                )}
            </React.Fragment>
          ),
          remarks: (
            <ConvertibleInput
              readOnly={!kpi?.editing?.remarks}
              fullWidth={true}
              sx={{
                flex: 1,
                gap: "6px",
                whiteSpace: "wrap",
                textAlign: "start",
                overflow: "hidden",
                textOverflow: "ellipsis",
                display: "-webkit-box",
                WebkitLineClamp: "3",
                WebkitBoxOrient: "vertical",
              }}
              multiline
              rows={2}
              typographyProps={{
                variant: "inherit",
                color: "dark",
              }}
              label={label("remark-plural")}
              waitForSave={true}
              onSave={kpi?.saveAnswerRemark}
              onCancel={kpi?.cancelAnswerRemark}
              value={
                !kpi?.editing?.remarks
                  ? kpi.auditKpis[0].answerRemark || ""
                  : kpi.auditKpis[0].answerRemarkTemp ||
                    kpi.auditKpis[0].answerRemark ||
                    ""
              }
              onChange={(e) => {
                const newFlatPillars = [...flatPillars];
                newFlatPillars[index].auditKpis[0].answerRemark =
                  e.target.value;
                setFlatPillars(newFlatPillars);
              }}
              includeUpdate={
                permissions?.update
                  ? {
                      cancelButtonProps: {
                        onClick: () => {
                          const newFlatPillars = [...flatPillars];
                          newFlatPillars[index].cancelAnswerRemark =
                            Math.random();
                          if (!newFlatPillars[index].editing)
                            newFlatPillars[index].editing = {};
                          newFlatPillars[index].editing.remarks = false;
                          setFlatPillars(newFlatPillars);
                        },

                        disabled: flatPillars[index].loading,
                      },
                      saveButtonProps: {
                        onClick: async () => {
                          const newFlatPillars = [...flatPillars];
                          //newFlatPillars[index].loading = true;
                          newFlatPillars[index].saveAnswerRemark =
                            Math.random();
                          if (!newFlatPillars[index].editing)
                            newFlatPillars[index].editing = {};
                          newFlatPillars[index].editing.remarks = false;
                          setFlatPillars(newFlatPillars);

                          //handleUpdateAllKpis(newFlatPillars);
                        },
                        disabled: flatPillars[index].loading,
                      },
                      editButtonProps: {
                        onClick: () => {
                          const newFlatPillars = [...flatPillars];
                          if (!newFlatPillars[index].editing)
                            newFlatPillars[index].editing = {};
                          newFlatPillars[index].editing.remarks = true;
                          setFlatPillars(newFlatPillars);
                        },
                      },
                    }
                  : null
              }
              key={`Remark for ${pillar.id} ${index}`}
            />
          ),
          attachments: (
            <Attachments
              getAudit={fetch}
              readOnly={!permissions.update}
              attachments={kpi.auditKpis[0].attachments || []}
              setAttachments={(attachments) => {
                const newFlatPillars = [...flatPillars];
                attachments = attachments.map((a) => {
                  if (!a.file) return a;

                  const newFile = new File(
                    [a.file],
                    `${kpi.auditKpis[0].id}_${a.file.name}`,
                    {
                      type: a.file.type,
                      kpiId: kpi.auditKpis[0].id,
                    },
                  );
                  return { ...a, file: newFile };
                });
                newFlatPillars[index].auditKpis[0].attachments = [
                  ...attachments,
                ];
                setFlatPillars(newFlatPillars);
              }}
              key={`Attachements: ${kpi.id}`}
            />
          ),
          actions: "",
        };
      } else if (!isKpi) {
        collapsedParentIds.push(pillar.id);
        return null;
      }
      return null;
    });

    const newF = f.filter((element) => element !== null);

    return newF;
  }

  const updatePillarsMemoized = useCallback(
    (flatPillars) => {
      return updateRows(flatPillars);
    },
    [flatPillars],
  );

  async function handleEditAuditInfo(newAudit) {
    if (!permissions.update) return;

    const [response, data] = await auditsService.updateAudit(
      audit.id,
      newAudit,
    );
    if (response.ok) {
      setAudit(data);
      setOldAudit({ ...data, pillars: [...data.pillars] });
      return { success: true };
    } else {
      console.log(`Error while editing audit info: ${data.error}`);
      return { error: data.error };
    }
  }

  async function handleEditAuditFirstAnsweredAt(newAudit) {
    const [response, data] = await auditsService.updateAuditFirstAnsweredAt(
      audit.id,
      newAudit,
    );
    if (response.ok) {
      await fetch();
      return { success: true };
    } else {
      console.log(`Error while editing audit info: ${data.error}`);
      return { error: data.error };
    }
  }

  async function handleEditAuditStatus(newAudit) {
    const [response, data] = await auditsService.updateAuditStatus(audit.id, {
      statusId: newAudit.statusId,
      force: true,
    });

    if (response.ok) {
      await fetch();
      return { success: true };
    } else {
      console.log(`Error while editing audit info: ${data.error}`);
      return { error: data.error };
    }
  }

  async function handleEditAuditKpis(auditKpis) {
    if (!permissions.update) return;
    /*const id = newAudit.id;
    newAudit = {
      auditKpis: newAudit.auditKpis,
    };
    const [response, data] = await auditsService.updateAudit(id, newAudit);
    if (response.ok) {
      setAudit(data);
      setOldAudit({ ...data, pillars: [...data.pillars] });
      return { success: true };
    } else {
      console.log(`Error while editing audit info: ${data.error}`);
      return { error: data.error };
    }*/

    const [response, data] = await auditsService.updateAuditBaseKpis(audit.id, {
      auditKpis: auditKpis.map((auditKpi) => {
        const item = audit?.auditKpis?.find(
          (item) => item.kpiId == auditKpi.kpiId,
        );

        if (item?.id) {
          auditKpi.id = item.id;
        }

        return auditKpi;
      }),
    });
    if (response.ok) {
      setAudit(JSON.parse(JSON.stringify(data)));
      setOldAudit(JSON.parse(JSON.stringify(data)));
      return { success: true };
    } else {
      console.log(`Error while editing audit info: ${data.error}`);
      return { error: data.error };
    }
  }

  async function handleUpdateAllKpis(
    updatedFlatPillars,
    updatedOldFlatPillars,
    updatedAudit,
  ) {
    if (!permissions.update) return;
    let _oldFlatPillars = oldFlatPillars;
    if (updatedOldFlatPillars) _oldFlatPillars = updatedOldFlatPillars;

    let _flatPillars = flatPillars;
    if (updatedFlatPillars) _flatPillars = updatedFlatPillars;

    let _audit = audit;
    if (updatedAudit) _audit = updatedAudit;

    let kpis = _flatPillars.filter((f) => {
      return !f.pillars;
    });

    let oldKpis = _oldFlatPillars.filter((f) => {
      return !f.pillars;
    });

    kpis = kpis.map((kpi) => {
      return kpi.auditKpis[0];
    });

    oldKpis = oldKpis.map((kpi) => {
      return kpi.auditKpis[0];
    });

    const toBeUpdatedKpis = [];
    oldKpis.map((oldKpi, index) => {
      if (JSON.stringify(oldKpi) != JSON.stringify(kpis[index])) {
        const kpi = { ...kpis[index] };
        const difference = jsondiffpatch.diff(oldKpi, kpi);
        const newKpi = { id: kpi.id, kpiId: kpi.kpiId };
        Object.keys(difference).map((key) => {
          newKpi[key] = kpi[key];
        });
        toBeUpdatedKpis.push(newKpi);
      }
    });

    if (toBeUpdatedKpis.length == 0) {
      return;
    }

    let files = toBeUpdatedKpis.flatMap((kpi) => {
      return kpi.attachments;
    });

    files = files?.map((file) => file?.file).filter((file) => file) || [];

    const [response, data] = await auditsService.updateAuditKpis(_audit.id, {
      body: toBeUpdatedKpis,
      files: files,
    });

    if (response.ok) {
      const newAudit = { ...audit, pillars: [...audit.pillars] };
      for (let i = 0; i < newAudit.pillars.length; i++) {
        for (let j = 0; j < newAudit.pillars[i].kpis?.length; j++) {
          const id = newAudit?.pillars[i]?.kpis[j]?.auditKpis[0]?.id;
          const newAuditKpi = data.find((auditKpi) => id == auditKpi.id);

          if (newAuditKpi) {
            newAudit.pillars[i].kpis[j].auditKpis[0] = newAuditKpi;
          }
        }
      }

      setAudit(newAudit);
      setOldAudit({ ...newAudit, pillars: [...newAudit.pillars] });
    } else {
      console.log(`Error while updating kpis`);
      throw new Error(data.error);
    }
  }

  async function onCompleted() {
    const audit = await fetch();

    if (
      !audit?.auditKpis?.find(
        (auditKpi) => auditKpi?.kpiValueTypeAnswerId == DEV_ANSWER_TYPE_ID,
      )
    ) {
      handleEditAuditInfo({ statusId: 4 });
      return;
    }

    const [response, data] = await auditsService.auditHasTasks(audit.id);
    if (response.ok) {
      if (!data) {
        changeOpen("tasks", true);
      } else {
        handleEditAuditInfo({ statusId: 4 });
      }
    }
  }

  const canGenerateReport = useMemo(() => {
    if (!audit) return;
    return GENERATE_REPORT_STATUS_IDS.includes(Number(audit.statusId));
  }, [audit?.statusId]);

  const table = useMemo(() => {
    return {
      columns: [
        {
          Header: label("description-singular"),
          accessor: "description",
          align: "left",
          width: "400px",

          bodyCellStyle: {
            minWidth: "400px",
            whiteSpace: "unset",
            overflow: "unset",
            textOverflow: "unset",
          },
        },
        ...[
          {
            Header: "Eliminatory",
            accessor: "eliminatory",
          },
        ],
        {
          Header: label("weight-singular"),
          accessor: "weight",
        },
        {
          Header: label("answer-singular"),
          accessor: "answer",
          align: "center",
          width: "300px",
          bodyCellStyle: {
            minWidth: "300px",
          },
        },
        ...[
          {
            Header: label("deviation-risk-action"),
            accessor: "selectDevRiskAction",
            align: "center",
            width: "400px",
            bodyCellStyle: {
              minWidth: "300px",
            },
          },
        ],

        {
          Header: label("remark-plural"),
          accessor: "remarks",
          align: "center",
          width: "400px",
          bodyCellStyle: {
            minWidth: "300px",
          },
        },
        {
          Header: label("attachment-plural"),
          accessor: "attachments",
          align: "center",
        },
      ],
      rows:
        (pillarRows || []).length == 0
          ? loading
            ? [
                {
                  description: (
                    <MDTypography variant="inherit" color="inherit">
                      {label("loading")}
                    </MDTypography>
                  ),
                },
              ]
            : [
                {
                  description: (
                    <MDTypography variant="inherit" color="inherit">
                      {label("no-kpis-for-category")}
                    </MDTypography>
                  ),
                },
              ]
          : pillarRows,
    };
  }, [pillarRows]);

  useEffect(() => {
    if (!audit || !audit.pillars) {
      setFlatPillars([]);
      setOldFlatPillars([]);
      return;
    }

    let oldCollapsed = new Object();
    if (flatPillars) {
      flatPillars.map((pillar) => {
        oldCollapsed["" + pillar.id] = pillar.collapsed;
      });
    }
    const flat = [];

    for (let i = 0; i < audit.pillars.length; i++) {
      depthFirstSearch(audit.pillars[i], 0, flat, oldCollapsed);
    }

    setFlatPillars(flat);

    console.log("FLAT: ", flat);
    setOldFlatPillars(JSON.parse(JSON.stringify(flat)));

    function depthFirstSearch(node, level = 0, arr, oldCollapsed, isKpi) {
      if (node !== null) {
        arr.push({
          ...node,
          level: level,
          collapsed: isKpi
            ? false
            : oldCollapsed[node.id] !== undefined
              ? oldCollapsed[node.id]
              : false,
        });
        if (node.pillars) {
          for (const child of node.pillars) {
            depthFirstSearch(child, level + 1, arr, oldCollapsed);
          }
        }
        if (node.kpis) {
          for (const child of node.kpis) {
            depthFirstSearch(child, level + 1, arr, oldCollapsed, true);
          }
        }
      }
    }
  }, [audit?.pillars]);

  useEffect(() => {
    if (flatPillars) updateRows(flatPillars);
  }, [open.assessmentReport]);

  useEffect(() => {
    const source = axios.CancelToken.source();
    fetch(source.token);
    fetchStatuses(source.token);
    return () => {
      source.cancel("Component unmounted");
    };
  }, []);

  useEffect(() => {
    if (flatPillars) {
      let ignoredFieldsFlatPillars = JSON.parse(JSON.stringify(flatPillars))
        .map((p) => {
          p.collapsed = "";
          p.editing = "";
          p.loading = "";
          p.saveDescription = "";
          p.saveAnswerRemark = "";
          p.cancelDescription = "";
          p.cancelAnswerRemark = "";

          if (p.auditKpis?.length > 0) {
            p.auditKpis[0].descriptionTemp = "";
            p.auditKpis[0].answerRemarkTemp = "";
          }

          return p;
        })
        .filter((p) => !p.pillars);

      let ignoredFieldsOldFlatPillars = JSON.parse(
        JSON.stringify(oldFlatPillars),
      )
        .map((p) => {
          p.collapsed = "";
          p.editing = "";
          p.loading = "";
          p.saveDescription = "";
          p.saveAnswerRemark = "";
          p.cancelDescription = "";
          p.cancelAnswerRemark = "";

          if (p.auditKpis?.length > 0) {
            p.auditKpis[0].descriptionTemp = "";
            p.auditKpis[0].answerRemarkTemp = "";
          }

          return p;
        })
        .filter((p) => !p.pillars);

      const diff = jsondiffpatch.diff(
        ignoredFieldsFlatPillars,
        ignoredFieldsOldFlatPillars,
      );

      setPillarRows(updatePillarsMemoized(flatPillars));

      if (diff) {
        save(flatPillars, oldFlatPillars, audit, () => {});
      }
    } else {
      setPillarRows([]);
    }
  }, [flatPillars]);

  useEffect(() => {
    if (oldAudit && setRoute) {
      setRoute(["audits", oldAudit?.description]);
    }
  }, [oldAudit]);

  useEffect(() => {
    if (!audit || !audit.pillars) {
      setFlatPillars([]);
      setOldFlatPillars([]);
      return;
    }

    let oldCollapsed = new Object();
    if (flatPillars) {
      flatPillars.map((pillar) => {
        oldCollapsed["" + pillar.id] = pillar.collapsed;
      });
    }
    const flat = [];

    for (let i = 0; i < audit.pillars.length; i++) {
      depthFirstSearch(audit.pillars[i], 0, flat, oldCollapsed);
    }

    setFlatPillars(flat);
    setOldFlatPillars(JSON.parse(JSON.stringify(flat)));

    function depthFirstSearch(node, level = 0, arr, oldCollapsed, isKpi) {
      if (node !== null) {
        arr.push({
          ...node,
          level: level,
          collapsed: isKpi
            ? false
            : oldCollapsed[node.id] !== undefined
              ? oldCollapsed[node.id]
              : false,
        });
        if (node.pillars) {
          for (const child of node.pillars) {
            depthFirstSearch(child, level + 1, arr, oldCollapsed);
          }
        }
        if (node.kpis) {
          for (const child of node.kpis) {
            depthFirstSearch(child, level + 1, arr, oldCollapsed, true);
          }
        }
      }
    }
  }, [audit?.pillars]);

  if (auditState == "deleted") {
    return <Deleted item="audit" />;
  }

  if (!audit)
    return (
      <Box
        sx={{
          width: "100%",
          height: "calc(100vh - 145px)",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <CircularProgress />
      </Box>
    );

  let header = (
    <Card sx={{ borderRadius: "24px" }}>
      <MDBox p={3} display="flex" flexDirection="column" gap={2}>
        {/* Header toolbar */}
        <MDBox
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          flexWrap="wrap"
          columnGap={2}
          rowGap={1.5}
        >
          {/* Title + edit */}
          <MDBox display="flex" alignItems="center" gap={1.25} minWidth={0}>
            <MDTypography variant="h5" noWrap>
              {label("info")}
            </MDTypography>
            {permissions?.update && (
              <IconButton
                size="small"
                aria-label={label("edit")}
                onClick={() => changeOpen("info", Math.random())}
              >
                <Icon>edit</Icon>
              </IconButton>
            )}
          </MDBox>

          {/* Primary actions (wrap on small screens) */}
          <MDBox
            display="flex"
            alignItems="center"
            gap={1}
            flexWrap="wrap"
            justifyContent="flex-end"
          >
            {audit?.auditTypeId == 2 ? (
              <AuditReceiptDialog audit={audit} refresh={fetch} />
            ) : null}
            {audit?.auditTypeId == 2 ? (
              <AuditNarrativeDialog audit={audit} refresh={fetch} />
            ) : null}
            <MDButton
              variant="gradient"
              color="info"
              size="small"
              onClick={() => {
                changeOpen("assessmentReport", false);
                changeOpen("summaryGroups", true);
              }}
              disabled={!permissions.update || loadingReport}
            >
              {label("summary-group-plural")}
            </MDButton>

            {canGenerateReport ? (
              <LoadingButton
                size="small"
                type="button"
                variant="gradient"
                color="info"
                loading={loadingReport}
                disabled={open.summaryGroups || loadingReport}
                onClick={async () => {
                  if (open.summaryGroups || loadingReport) return;
                  setLoadingReport(true);
                  try {
                    const refreshedAudit = await fetch();
                    if (refreshedAudit) changeOpen("assessmentReport", true);
                  } finally {
                    setLoadingReport(false);
                  }
                }}
              >
                {label("generate-audit-report-button")}
              </LoadingButton>
            ) : null}
          </MDBox>
        </MDBox>

        {/* Context row: Branch/Group + Date */}
        <MDBox
          display="grid"
          alignItems="center"
          gap={1}
          sx={{
            gridTemplateColumns: { xs: "1fr", md: "1fr auto" },
          }}
        >
          <MDTypography variant="h6" sx={{ minWidth: 0 }}>
            {(audit?.branch?.branchGroup?.name || "") +
              " - " +
              (audit?.branch?.name || "")}
          </MDTypography>

          <MDTypography
            variant="subtitle2"
            sx={{ fontWeight: 400, textAlign: { xs: "left", md: "right" } }}
          >
            {moment(new Date(audit?.date)).format("MMMM Do YYYY, h:mm a")}
          </MDTypography>
        </MDBox>

        {/* Description + badges + tasks link */}
        <MDBox
          display="grid"
          alignItems="center"
          gap={1}
          sx={{
            gridTemplateColumns: { xs: "1fr", md: "1fr auto" },
          }}
        >
          <MDTypography variant="h5" sx={{ wordBreak: "break-word" }}>
            {audit?.description}
          </MDTypography>

          <MDBox
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: { xs: "flex-start", md: "flex-end" },
              gap: 2,
              flexWrap: "wrap",
            }}
          >
            {audit?.auditTypeId == 1 && (
              <MDTypography
                variant="h6"
                sx={{
                  backgroundColor: theme.palette.primary.light,
                  color: theme.palette.primary.contrastText,
                  borderRadius: "8px",
                  px: 1.25,
                  py: "2px",
                  whiteSpace: "nowrap",
                }}
              >
                {label("scoreless-audit")}
              </MDTypography>
            )}

            {audit?.auditTypeId == 2 && (
              <MDTypography
                variant="h6"
                sx={{
                  backgroundColor: theme.palette.primary.light,
                  color: theme.palette.primary.contrastText,
                  borderRadius: "8px",
                  px: 1.25,
                  py: "2px",
                  whiteSpace: "nowrap",
                }}
              >
                Mystery
              </MDTypography>
            )}

            {audit?.taskCount > 0 && (
              <Link
                component={RouterLink}
                target="_blank"
                rel="noopener noreferrer"
                to={`/tasks?audits=${audit.id}`}
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 0.5,
                  whiteSpace: "nowrap",
                }}
              >
                <Typography
                  sx={{
                    fontSize: "16px",
                    fontWeight: 500,
                    textDecoration: "underline !important",
                  }}
                >
                  {`View tasks (${audit.taskCount})`}
                </Typography>
                <OpenInNew fontSize="small" />
              </Link>
            )}
          </MDBox>
        </MDBox>

        {/* Assignment + status/actions */}
        <MDBox
          display="grid"
          alignItems="center"
          gap={1}
          sx={{
            gridTemplateColumns: { xs: "1fr auto auto", md: "auto 1fr auto" },
          }}
        >
          {/* Assignee pill */}
          <MDTypography
            variant="h6"
            sx={{
              backgroundColor: theme.palette.primary.light,
              color: theme.palette.primary.contrastText,
              borderRadius: "8px",
              width: "auto",
              px: 1.75,
              py: "2px",
              textOverflow: "ellipsis",
              overflow: "hidden",
              whiteSpace: "nowrap",
            }}
            title={audit?.assignedTo?.name}
          >
            {audit?.assignedTo?.name}
          </MDTypography>

          {/* Spacer on md+ so buttons anchor right */}
          <span />

          {/* Task creation + status menu */}
          <MDBox
            display="flex"
            alignItems="center"
            justifyContent={{ xs: "flex-start", md: "flex-end" }}
            gap={1}
            flexWrap="wrap"
          >
            {audit?.auditKpis?.find(
              (auditKpi) =>
                auditKpi?.kpiValueTypeAnswerId == DEV_ANSWER_TYPE_ID &&
                !auditKpi.tasks?.length,
            ) &&
            !DONT_GENERATE_TASKS_STATUS_IDS.includes(audit.statusId) &&
            can("create", "task") ? (
              <MDButton
                variant="gradient"
                color="info"
                size="small"
                onClick={() => changeOpen("tasks", Math.random())}
              >
                {label("create-tasks-button")}
              </MDButton>
            ) : null}

            <CustomizedMenus
              loading={loading}
              setLoading={setLoading}
              statuses={options.statuses}
              readOnly={!can("updateStatus", "audit")}
              currentStatus={{
                id: audit?.status?.id,
                description: audit?.status?.description,
              }}
              onChange={(newStatusId) => {
                // if (newStatusId == 4) {
                //   return onCompleted();
                // }
                return handleEditAuditStatus({ statusId: newStatusId });
                // return handleEditAuditInfo({ statusId: newStatusId });
              }}
            />
          </MDBox>
        </MDBox>

        {/* On-site duration (right-aligned) */}
        {audit?.siteOut && audit?.siteIn && (
          <MDBox display="flex" justifyContent="flex-end">
            <MDTypography
              variant="subtitle2"
              sx={{
                fontWeight: 400,
                display: "flex",
                alignItems: "center",
                gap: 1,
                flexWrap: "wrap",
                textAlign: "right",
              }}
            >
              {label("time-taken-on-site")}:
              <MDTypography variant="h6" component="span">
                {formatTimePeriod(
                  new Date(audit.siteOut) - new Date(audit.siteIn),
                  label,
                )}
              </MDTypography>
            </MDTypography>
          </MDBox>
        )}

        {/* Footer meta */}
        <Typography sx={{ fontSize: 14, m: 0 }}>
          {label("last-updated")}{" "}
          {[
            audit?.updatedBy?.name,
            moment(new Date(audit?.updatedAt)).format("D/M/YYYY, h:mm a"),
          ]
            .filter(Boolean)
            .join(" - ")}
        </Typography>
      </MDBox>
    </Card>
  );

  return (
    <>
      {open.tasks ? (
        <TasksDialog
          auditId={audit.id}
          open={open.tasks}
          setOpen={(v) => changeOpen("tasks", v)}
          auditKpis={audit?.auditKpis}
          accountId={audit?.branch?.branchGroup?.accountId}
          onSubmitSuccess={(data) => {
            setAudit(data);
            setOldAudit({ ...data, pillars: [...data.pillars] });
          }}
          onComplete={async () => {
            // await handleEditAuditInfo({ statusId: 4 });
            changeOpen("tasks", false);
          }}
        />
      ) : null}
      {open.summaryGroups ? (
        <AuditSummaryGroupsDialog
          setAudit={setAudit}
          isOpen={open.summaryGroups}
          audit={audit}
          onClose={() => {
            changeOpen("summaryGroups", false);
            changeOpen("assessmentReport", false);
          }}
        />
      ) : null}
      {open.assessmentReport && !open.summaryGroups ? (
        <AuditGenerateReportDialog
          isOpen={open.assessmentReport}
          audit={audit}
          contacts={options.contacts}
          fetchContacts={fetchContacts}
          handleEditAuditInfo={handleEditAuditInfo}
          handleEditAuditFirstAnsweredAt={handleEditAuditFirstAnsweredAt}
          onClose={() => {
            changeOpen("assessmentReport", false);
          }}
        />
      ) : null}
      {open.info ? (
        <AuditInfoDialogv2
          open={open.info}
          setOpen={(v) => changeOpen("info", v)}
          audit={audit}
          onSubmit={handleEditAuditInfo}
        />
      ) : null}
      {open.kpis ? (
        <AuditKpisDialogv2
          open={open.kpis}
          setOpen={(v) => changeOpen("kpis", v)}
          audit={audit}
          onSubmit={handleEditAuditKpis}
        />
      ) : null}

      <MDBox display="flex" flexDirection="column" gap={1}>
        {header}
        <Card sx={{ borderRadius: "24px" }}>
          <MDBox p={3}>
            <MDBox
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              gap={2}
              flexWrap="wrap"
            >
              <MDBox display="flex" alignItems="center" gap={2}>
                <MDTypography variant="h5">{label("kpi-plural")}</MDTypography>
                {permissions?.update && audit?.statusId === 1 && (
                  <IconButton
                    size="small"
                    onClick={() => {
                      changeOpen("kpis", Math.random());
                    }}
                  >
                    <Icon>edit</Icon>
                  </IconButton>
                )}
              </MDBox>
              <MDBox display="flex" alignItems="center" gap={0.5}>
                <Tooltip title="Zoom out">
                  <span>
                    <IconButton
                      size="small"
                      disabled={kpiZoom <= 0.5}
                      onClick={() => {
                        setKpiZoom((value) =>
                          Math.max(0.5, Number((value - 0.1).toFixed(1))),
                        );
                      }}
                    >
                      <Icon>zoom_out</Icon>
                    </IconButton>
                  </span>
                </Tooltip>
                <MDTypography
                  variant="caption"
                  color="secondary"
                  sx={{ minWidth: "38px", textAlign: "center" }}
                >
                  {Math.round(kpiZoom * 100)}%
                </MDTypography>
                <Tooltip title="Zoom in">
                  <span>
                    <IconButton
                      size="small"
                      disabled={kpiZoom >= 1.3}
                      onClick={() => {
                        setKpiZoom((value) =>
                          Math.min(1.3, Number((value + 0.1).toFixed(1))),
                        );
                      }}
                    >
                      <Icon>zoom_in</Icon>
                    </IconButton>
                  </span>
                </Tooltip>
              </MDBox>
            </MDBox>
            <MDBox sx={{ zoom: kpiZoom }}>
              <DataTable
                table={table}
                isSorted={false}
                entriesPerPage={{ defaultValue: 500 }}
                showTotalEntries={false}
                noEndBorder
              />
            </MDBox>
          </MDBox>
        </Card>
      </MDBox>
    </>
  );
}

function formatTimePeriod(ms, label) {
  const seconds = Math.floor((ms / 1000) % 60);
  const minutes = Math.floor((ms / (1000 * 60)) % 60);
  const hours = Math.floor((ms / (1000 * 60 * 60)) % 24);
  const days = Math.floor(ms / (1000 * 60 * 60 * 24));

  const formattedDays =
    days > 0
      ? `${days} ${days != 1 ? label("day-plural") : label("day-singular")}, `
      : "";
  const formattedHours =
    hours > 0
      ? `${hours} ${hours != 1 ? label("hour-plural") : label("hour-singular")}, `
      : "";
  const formattedMinutes =
    minutes > 0
      ? `${minutes} ${minutes != 1 ? label("minute-plural") : label("minute-singular")} ${label("and")} `
      : "";
  const formattedSeconds = `${seconds} ${seconds != 1 ? label("second-plural") : label("second-singular")}`;

  return `${formattedDays}${formattedHours}${formattedMinutes}${formattedSeconds}`;
}
